/**
 * Cart guard.
 *
 * The add-ons are ordinary Shopify products, so on their own they are
 * purchasable like anything else. This keeps them as genuine add-ons:
 *
 *   1. Their product pages redirect to the main product.
 *   2. Checkout is blocked whenever the cart holds add-ons but no main item
 *      (any product that is not on the add-on list).
 *   3. Express checkout buttons are hidden while blocked, since they skip
 *      the cart entirely and cannot be intercepted from outside their frame.
 *
 * This is enforcement at the storefront. Someone determined could still POST
 * straight to /checkout — true server-side enforcement needs a Shopify
 * cart-validation function, which lives in an app rather than a theme.
 */
(function () {
  'use strict';

  var cfg = window.novaGuard || {};
  // Where an add-on's own product page redirects to.
  var MAIN = String(cfg.main || '').trim();
  var ADDONS = (cfg.addons || []).map(function (h) { return String(h).trim(); }).filter(Boolean);
  var MESSAGE = cfg.message || 'Add a calendar to your order before checking out.';

  if (!MAIN || !ADDONS.length) return;

  var CHECKOUT = 'a[href*="/checkout"], button[name="checkout"], [data-nova-checkout]';
  var EXPRESS = '.additional-checkout-buttons, [data-shopify="payment-button"], shopify-accelerated-checkout, shopify-accelerated-checkout-cart';

  function route(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return (base.slice(-1) === '/' ? base : base + '/') + path;
  }

  function isAddon(handle) { return ADDONS.indexOf(handle) !== -1; }

  // ---- 1. Add-on product pages are not destinations ----
  var onProduct = location.pathname.match(/\/products\/([^/?#]+)/);
  if (onProduct && isAddon(decodeURIComponent(onProduct[1]))) {
    location.replace(route('products/' + MAIN));
    return;
  }

  // ---- 2. Read the cart and decide ----
  function inspect(cart) {
    var items = cart.items || [];
    var hasMain = false;
    var addonLines = [];

    // Any item that is not itself an add-on (a calendar, the robe, anything
    // added later) is something the add-ons can ride along with.
    items.forEach(function (item, i) {
      if (isAddon(item.handle)) addonLines.push(i + 1);
      else hasMain = true;
    });

    return { hasMain: hasMain, addonLines: addonLines, count: items.length };
  }

  function dropOrphans(lines) {
    // Remove from the bottom up so the earlier line numbers stay valid.
    return lines.slice().sort(function (a, b) { return b - a; })
      .reduce(function (chain, line) {
        return chain.then(function () {
          return fetch(route('cart/change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: line, quantity: 0 })
          });
        });
      }, Promise.resolve());
  }

  function paint(blocked) {
    document.querySelectorAll(CHECKOUT).forEach(function (el) {
      el.classList.toggle('is-blocked', blocked);
      if (blocked) { el.setAttribute('aria-disabled', 'true'); }
      else { el.removeAttribute('aria-disabled'); }
    });

    // Express buttons render in their own frames — hide rather than intercept.
    document.querySelectorAll(EXPRESS).forEach(function (el) { el.hidden = blocked; });
  }

  /**
   * Say why the cart changed. This lives in the drawer body rather than the
   * foot because clearing orphans empties the cart, and the foot is hidden
   * in that state — which is precisely when the explanation is needed.
   */
  function notify(text) {
    var body = document.querySelector('[data-nova-drawer-body]');
    if (!body) return;

    var note = body.querySelector('[data-nova-guard-note]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'nova-guard-note';
      note.setAttribute('data-nova-guard-note', '');
      note.setAttribute('role', 'status');
      body.insertBefore(note, body.firstChild);
    }

    note.textContent = text;
    note.hidden = false;

    window.clearTimeout(notify._t);
    notify._t = window.setTimeout(function () { note.hidden = true; }, 6000);
  }

  var blocked = false;

  function refresh() {
    return fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var state = inspect(cart);

        // Add-ons with nothing to attach to: clear them out.
        if (!state.hasMain && state.addonLines.length) {
          return dropOrphans(state.addonLines).then(function () {
            blocked = false;
            paint(false);
            notify(MESSAGE);
            document.dispatchEvent(new CustomEvent('nova:cart-guarded'));
          });
        }

        blocked = !state.hasMain && state.count > 0;
        paint(blocked);
      })
      .catch(function () { /* if the check fails, leave the cart alone */ });
  }

  document.addEventListener('click', function (e) {
    if (!blocked) return;
    if (e.target.closest && e.target.closest(CHECKOUT)) {
      e.preventDefault();
      e.stopPropagation();
      paint(true);
      notify(MESSAGE);
    }
  }, true);

  document.addEventListener('submit', function (e) {
    var action = e.target.getAttribute && e.target.getAttribute('action');
    if (blocked && action && action.indexOf('/cart') !== -1) e.preventDefault();
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
  ['nova:cart-updated', 'nova:cart-changed'].forEach(function (evt) {
    document.addEventListener(evt, function () { window.setTimeout(refresh, 150); });
  });
})();
